import { Activity } from "@/features/planner/api/planner";
import { Box, Card, Chip, IconButton, Typography } from "@mui/material";
import { MdLocationOn } from "react-icons/md";

interface MustDoCardProps {
    activity?: Activity;
}

export default function MustDoCard({ activity }: MustDoCardProps) {
    const {
        title = "Notre-Dame Cathedral Visit",
        attraction_name: attractionName,
        activity: description = "Start your day with a visit to the historic Notre-Dame Cathedral, taking in its Gothic architecture and learning about its rich history. The clear morning light adds to the beauty of the cathedral's stained glass windows.",
        duration = "1-2 hours",
        price = "20",
        is_free: isFree = false,
        why_special: whySpecial = "day with a visit to the historic Notre-Dame Cathedral, taking in its Gothic architecture and learning about its rich history. The clear morning light adds to the beauty of the cathedral's stained glass windows.",
        best_time: bestTime = "Evening (5.00 PM)",
        insider_tip: insiderTips = "day with a visit to the historic Notre-Dame Cathedral, taking in its Gothic architecture and learning about its rich history. The clear morning light adds to the beauty of the cathedral's stained glass windows.",
        difficulty,
        wheelchair_accessible: wheelchairAccessible,
        fitness_required: fitnessRequired,
        accessibility_notes: accessibilityNotes,
        peak_hours: peakHours,
        quietest_time: quietestTime,
        crowd_level: crowdLevel,
        quick_info: quickInfo,
    } = activity || {};

    return (
        <Card
            className="relative overflow-hidden"
            sx={{
                backgroundColor: "#f5f1ed",
                borderRadius: "16px",
                padding: { xs: "16px", md: "24px" },
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
                    transform: "translateY(-2px)",
                },
                width: { xs: "100%", md: "380px" },
                flexShrink: 0,
            }}
        >
            <div className="flex items-start justify-between">
                {/* Title */}
                <Typography
                    variant="h5"
                    sx={{
                        color: "#1a9a9a",
                        fontWeight: 600,
                        fontSize: { xs: "20px", md: "24px" },
                        marginBottom: "8px",
                        lineHeight: 1.3,
                    }}
                >
                    {title}
                </Typography>
                {/* Location Button - Bottom Right */}
                <IconButton
                    sx={{
                        backgroundColor: "#ff6b35",
                        color: "white",
                        width: "36px",
                        height: "36px",
                        transition: "all 0.3s ease",
                        "&:hover": {
                            backgroundColor: "#e55a28",
                            transform: "scale(1.05)",
                        },
                    }}
                    onClick={() => {
                        const mapUrl = activity?.map_location ?? '';
                        if (mapUrl) {
                            window.open(mapUrl, '_blank', 'noopener,noreferrer');
                        }
                    }}
                >
                    <MdLocationOn style={{ fontSize: "28px" }} />
                </IconButton>
            </div>

            {/* Attraction Name */}
            {attractionName && (
                <Typography
                    sx={{
                        color: "#666",
                        fontSize: "14px",
                        marginBottom: "12px",
                        fontStyle: "italic",
                    }}
                >
                    📍 {attractionName}
                </Typography>
            )}

            {/* Quick Info Bar */}
            {quickInfo && (
                <Box className="flex flex-wrap gap-2 mb-4">
                    {quickInfo.rating && (
                        <Chip
                            label={quickInfo.rating.display}
                            size="small"
                            sx={{
                                backgroundColor: "#FFF8E1",
                                color: "#F57C00",
                                fontWeight: 500,
                                fontSize: "12px",
                            }}
                        />
                    )}
                    {quickInfo.location && (
                        <Chip
                            label={quickInfo.location.display}
                            size="small"
                            sx={{
                                backgroundColor: "#EDE7F6",
                                color: "#512DA8",
                                fontWeight: 500,
                                fontSize: "12px",
                            }}
                        />
                    )}
                    {quickInfo.budget && (
                        <Chip
                            label={quickInfo.budget.display}
                            size="small"
                            sx={{
                                backgroundColor: "#E8F5E9",
                                color: "#2E7D32",
                                fontWeight: 500,
                                fontSize: "12px",
                            }}
                        />
                    )}
                </Box>
            )}

            {/* Description */}
            <Typography
                sx={{
                    color: "#333",
                    fontSize: { xs: "14px", md: "16px" },
                    lineHeight: 1.6,
                    marginBottom: "20px",
                }}
            >
                {description}
            </Typography>

            {/* Duration */}
            <Box className="mb-3">
                <Typography
                    component="span"
                    sx={{
                        color: "#ff6b35",
                        fontWeight: 600,
                        fontSize: "16px",
                    }}
                >
                    Duration:{" "}
                </Typography>
                <Typography
                    component="span"
                    sx={{
                        color: "#333",
                        fontSize: "16px",
                    }}
                >
                    {duration}
                </Typography>
            </Box>

            {/* Price and Is Free Row */}
            <Box className="flex items-center gap-8 mb-3">
                <Box>
                    <Typography
                        component="span"
                        sx={{
                            color: "#ff6b35",
                            fontWeight: 600,
                            fontSize: "16px",
                        }}
                    >
                        Price:{" "}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            color: "#333",
                            fontSize: "16px",
                        }}
                    >
                        {isFree ? "Free" : price}
                    </Typography>
                </Box>
            </Box>

            {/* Why Special */}
            <Box className="mb-3">
                <Typography
                    component="span"
                    sx={{
                        color: "#ff6b35",
                        fontWeight: 600,
                        fontSize: "16px",
                    }}
                >
                    Why Special:{" "}
                </Typography>
                <Typography
                    component="span"
                    sx={{
                        color: "#333",
                        fontSize: "16px",
                        lineHeight: 1.6,
                    }}
                >
                    {whySpecial}
                </Typography>
            </Box>

            {/* Best Time */}
            <Box className="mb-3">
                <Typography
                    component="span"
                    sx={{
                        color: "#ff6b35",
                        fontWeight: 600,
                        fontSize: "16px",
                    }}
                >
                    Best Time:{" "}
                </Typography>
                <Typography
                    component="span"
                    sx={{
                        color: "#333",
                        fontSize: "16px",
                    }}
                >
                    {bestTime}
                </Typography>
            </Box>

            {/* Insider Tips */}
            <Box className="mb-4">
                <Typography
                    component="span"
                    sx={{
                        color: "#ff6b35",
                        fontWeight: 600,
                        fontSize: "16px",
                    }}
                >
                    Insider Tips:{" "}
                </Typography>
                <Typography
                    component="span"
                    sx={{
                        color: "#333",
                        fontSize: "16px",
                        lineHeight: 1.6,
                    }}
                >
                    {insiderTips}
                </Typography>
            </Box>

            {/* Difficulty and Wheelchair Accessibility Row */}
            {(difficulty || wheelchairAccessible) && (
                <Box className="flex items-center gap-8 mb-3">
                    {difficulty && (
                        <Box>
                            <Typography
                                component="span"
                                sx={{
                                    color: "#ff6b35",
                                    fontWeight: 600,
                                    fontSize: "16px",
                                }}
                            >
                                Difficulty:{" "}
                            </Typography>
                            <Typography
                                component="span"
                                sx={{
                                    color: "#333",
                                    fontSize: "16px",
                                    textTransform: "capitalize",
                                }}
                            >
                                {difficulty}
                            </Typography>
                        </Box>
                    )}
                    {wheelchairAccessible && (
                        <Box>
                            <Typography
                                component="span"
                                sx={{
                                    color: "#ff6b35",
                                    fontWeight: 600,
                                    fontSize: "16px",
                                }}
                            >
                                Wheelchair:{" "}
                            </Typography>
                            <Typography
                                component="span"
                                sx={{
                                    color: "#333",
                                    fontSize: "16px",
                                    textTransform: "capitalize",
                                }}
                            >
                                {wheelchairAccessible}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Fitness Required */}
            {fitnessRequired && (
                <Box className="mb-3">
                    <Typography
                        component="span"
                        sx={{
                            color: "#ff6b35",
                            fontWeight: 600,
                            fontSize: "16px",
                        }}
                    >
                        Fitness Required:{" "}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            color: "#333",
                            fontSize: "16px",
                        }}
                    >
                        {fitnessRequired}
                    </Typography>
                </Box>
            )}

            {/* Accessibility Notes */}
            {accessibilityNotes && (
                <Box className="mb-3">
                    <Typography
                        component="span"
                        sx={{
                            color: "#ff6b35",
                            fontWeight: 600,
                            fontSize: "16px",
                        }}
                    >
                        Accessibility:{" "}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            color: "#333",
                            fontSize: "16px",
                        }}
                    >
                        {accessibilityNotes}
                    </Typography>
                </Box>
            )}

            {/* Peak Hours */}
            {peakHours && (
                <Box className="mb-3">
                    <Typography
                        component="span"
                        sx={{
                            color: "#ff6b35",
                            fontWeight: 600,
                            fontSize: "16px",
                        }}
                    >
                        Peak Hours:{" "}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            color: "#333",
                            fontSize: "16px",
                        }}
                    >
                        {peakHours}
                    </Typography>
                </Box>
            )}

            {/* Quietest Time */}
            {quietestTime && (
                <Box className="mb-3">
                    <Typography
                        component="span"
                        sx={{
                            color: "#ff6b35",
                            fontWeight: 600,
                            fontSize: "16px",
                        }}
                    >
                        Quietest Time:{" "}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            color: "#333",
                            fontSize: "16px",
                        }}
                    >
                        {quietestTime}
                    </Typography>
                </Box>
            )}

            {/* Crowd Level */}
            {crowdLevel && (
                <Box className="mb-3">
                    <Typography
                        component="span"
                        sx={{
                            color: "#ff6b35",
                            fontWeight: 600,
                            fontSize: "16px",
                        }}
                    >
                        Crowd Level:{" "}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            color: "#333",
                            fontSize: "16px",
                            textTransform: "capitalize",
                        }}
                    >
                        {crowdLevel}
                    </Typography>
                </Box>
            )}

            {/* Quick Info Links */}
            {quickInfo && (quickInfo.website?.url || quickInfo.phone?.number) && (
                <Box className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex flex-wrap gap-3">
                        {quickInfo.website?.url && (
                            <a
                                href={quickInfo.website.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-[#1a9a9a] hover:underline"
                            >
                                🌐 {quickInfo.website.display}
                            </a>
                        )}
                        {quickInfo.phone?.number && (
                            <a
                                href={`tel:${quickInfo.phone.number}`}
                                className="text-sm text-[#1a9a9a] hover:underline"
                            >
                                📞 {quickInfo.phone.display}
                            </a>
                        )}
                    </div>
                </Box>
            )}
        </Card>
    );
}
